import * as React from 'react';
import { isMobile } from 'react-device-detect';
import { Helmet } from 'react-helmet';
import { Route, Switch } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import * as Sentry from '@sentry/react';

import {SessionExtend, handleTimeoutToDisplaySessionExtendWidget, extendSessionAtInterval} from '@weddingspot/ws-core-components';
import {
    ToastProvider,
    FeaturedFlagsContextProvider,
    Header,
    HeaderContextProvider,
    WSAppContextProvider,
    Footer,
    getUserProps
} from '@weddingspot/ws-core-components';
const { ThemeProvider } = require('@cvent/carina-theme-provider');
import { ThemeProvider as V1ThemeProvider } from '@cvent/carina/components/ThemeProvider';
import wsCarinaTheme from './configs/carina-theme';

import getRoutes from './urls/routes';
import IosBannerContainer from './containers/IosAppBanner';
import ErrorBoundary from './containers/ErrorBoundary';

import * as URLS from './urls';
import { connect } from 'react-redux';

import {
    META_ROBOTS,
    APP_ROOT_DOM_NODE_ID,
    TYPEAHEAD_DATA_TTL, HOGAN_CONFIG
} from './constants/config';
import { HOTELS_LANDING_PAGE, HOTELS_SRP } from './urls/hotels-urls';
import ShoppingCartContextProviderWrapper from './wrappers/ShoppingCartContextProviderWrapper';
import GlobalLogger from './utils/GlobalLogger';
import { logEvent, AnalyticsEvent } from '@weddingspot/ws-analytics';
import { HeaderSRP, HeaderVenueProfile } from './components/headers';

import { datadogRum } from '@datadog/browser-rum';

function initializeDatadogRum(props: AppProps) {
    const tracingOriginsRegexp = /^(http(s)?:\/\/)?(sg50|ap50|ts50)?\.wedding-spot\.com(.*)$|^(http(s)?:\/\/)?(www.)?\wedding-spot\.com(.*)|^(http(s)?:\/\/)?ws-(render|next|core)-(ap50-|sg50-|ts50-)?ws(dv|pr)\.(us-east-1|us-west-2)\.lb\.(cvent\.com)(.*)$/gm
    const excludedActivityUrlsRegex = /(.*)(amplitude)(.*)|(.*)(analytics.google)(.*)|(.*)(google-analytics)(.*)|(.*)(clarity.ms)(.*)|(.*)(pinterest)(.*)|(.*)(doubleclick)(.*)|(.*)(salesloft)(.*)/gm
    datadogRum.init({
        applicationId: HOGAN_CONFIG.datadog.applicationId,
        clientToken: HOGAN_CONFIG.datadog.clientToken,
        site: 'datadoghq.com',
        service: props.ddService || 'ws-render',
        env: props.ddEnv,
        version: props.ddVersion, // deployed version of the application
        sessionSampleRate: Number(HOGAN_CONFIG.datadog.sessionSampleRate),
        sessionReplaySampleRate: Number(HOGAN_CONFIG.datadog.sessionReplaySampleRate),
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls: [tracingOriginsRegexp],
        trackSessionAcrossSubdomains: true,
        useSecureSessionCookie: true,
        useCrossSiteSessionCookie: true, // This is deprecated in v5 in favor of usePartitionedCrossSiteSessionCookie,
        // but there is a potential breaking issue with use of that param. Please refer:
        // https://github.com/cvent-internal/development-framework/pull/3397/files#diff-b828bf5b40be25b10e9d4dc4f03da7271d4fc404f8660c8390acd4a7bafdd309R17
        // and https://cvent.slack.com/archives/C01EWNQ7K5E/p1718295869782689?thread_ts=1718122761.529549&cid=C01EWNQ7K5E
        traceSampleRate: 25,
        traceContextInjection: 'sampled',
        excludedActivityUrls: [excludedActivityUrlsRegex],
    })
}

function setDDGlobalContext(props: AppProps){
    const env = props.ddEnv || 'local';
    const version = props.ddVersion || 'undefined';
    getUserProps(env,version).then((userProps: any) => {
        Object.entries(userProps).forEach(([key, value]) => {
            datadogRum.setGlobalContextProperty(key, value)
        })
    });
}

require('./styles/index.scss');
require('./styles/indexCss.scss');
require('./App.module.scss');

interface AppProps {
    history: any;
    isBot?: boolean;
    profileVersion?: string;
    metaRobots?: string;
    ddService?: string;
    ddEnv?: string;
    ddVersion?: string;
    nonceValue?: string;
}
interface State {
    isOpen:boolean;
}

class App extends React.Component<AppProps, State> {
    constructor(props: AppProps) {
        super(props);
        // let applicationConfig = props;
        initializeDatadogRum(props);
        this.state = {
            isOpen: false
        };
    }

    setIsOpen = (val:boolean)=>{
        this.setState({
            isOpen:val
        })
    }

    componentDidMount() {
        setDDGlobalContext(this.props);
        extendSessionAtInterval();
        handleTimeoutToDisplaySessionExtendWidget(this.setIsOpen);
    }

    render() {
        const defaultHeaderContextProps = {
            headerItemClicked: (item: string) => logEvent(AnalyticsEvent.EVTNM_CLICKED_HEADER_ITEM, { Item: item }),
            headerLocationItemClicked: (source: string, value: string) =>
                logEvent(AnalyticsEvent.EVTNM_CLICKED_LOCATIONS_ITEM, { Source: source, Value: value }),
            onSearchResultClicked: (link: string, inputValue: string) =>
                logEvent(AnalyticsEvent.EVTNM_CLICKED_SEARCH, { Link: link }, undefined, inputValue),
            dataUrl: URLS.TYPEAHEAD_CDN_URL,
            dataTTL: TYPEAHEAD_DATA_TTL,
            options: {
                maxResultsPerSection: 5,
                locationUrlFormatter: URLS.getLoacationFullURL,
                venueUrlFormatter: URLS.getVenueURL,
                styleUrlFormatter: URLS.getStyleSrpUrl,
            },
            profileVersion: this.props.profileVersion,
            nonceValue: this.props.nonceValue,
        };

        return (
            <div className='App'>
                <WSAppContextProvider
                    rootNodeId={APP_ROOT_DOM_NODE_ID}
                    ccpaDoNotSellUrl={URLS.CCPA_DO_NOT_SELL_URL}
                    onOpenAuthModal={(src) => {
                        // 'Forced' event prop is hard-coded because this is a property added by the django
                        // app, at the time of writing this the react auto modal doesn't have a "forced login" mode
                        logEvent(AnalyticsEvent.EVTNM_SHOW_REGISTRATION_MODAL, { Forced: false, Source: src });
                    }}
                    onCreateNewAccountSuccess={() =>
                        logEvent(AnalyticsEvent.EVTNM_REGISTERED, {
                            'Is Mobile': isMobile,
                            'Signup Type': 'Email Signup',
                            Source: 'Header',
                        })
                    }
                >
                    <V1ThemeProvider theme={wsCarinaTheme}>
                        <ThemeProvider theme={wsCarinaTheme}>
                            <ToastProvider>
                                <FeaturedFlagsContextProvider isBot={this.props.isBot}>
                                    <HeaderContextProvider {...defaultHeaderContextProps}>
                                        <ShoppingCartContextProviderWrapper>
                                            <GlobalLogger />
                                            <Helmet
                                                defaultTitle='Wedding Venues - Price & Compare Top Venues | Wedding Spot'
                                                meta={[
                                                    { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1' },
                                                    { name: 'robots', content: this.props.metaRobots ? this.props.metaRobots : META_ROBOTS },
                                                ]}
                                                htmlAttributes={{ lang: 'en' }}
                                            />

                                            <IosBannerContainer />
                                            <SessionExtend isOpen={this.state.isOpen} setIsOpen = {this.setIsOpen}/>
                                            <Switch>
                                                <Route path={URLS.VENDOR_SIGNUP} render={() => <Header collapsed={true} />} />
                                                <Route
                                                    path={URLS.ALL_STYLES_LANDING_PAGE}
                                                    render={() => <Header isWhiteStyle={true} readyForSplash={true} />}
                                                />
                                                <Route path={URLS.ALL_VENUE_PROFILE_URLS} component={HeaderVenueProfile} />
                                                <Route path={URLS.ALL_SRP_URLS} exact={true} component={HeaderSRP} />
                                                <Route path={HOTELS_SRP} render={() => <Header hideTypeahead={true} />} />
                                                <Route
                                                    path={HOTELS_LANDING_PAGE}
                                                    render={() => <Header overlayStyle={true} hideTypeahead={true} />}
                                                />
                                                <Route render={() => <Header />} />
                                            </Switch>
                                            <div className='App--route-content'>
                                                <ErrorBoundary>{renderRoutes(getRoutes())}</ErrorBoundary>
                                            </div>
                                            <Switch>
                                                <Route path={URLS.RFP_EMAIL} render={() => null} />
                                                <Route path={HOTELS_SRP} render={() => null} />
                                                <Route path={URLS.CHECKOUT} render={() => null} />
                                                <Footer />
                                            </Switch>
                                        </ShoppingCartContextProviderWrapper>
                                    </HeaderContextProvider>
                                </FeaturedFlagsContextProvider>
                            </ToastProvider>
                        </ThemeProvider>
                    </V1ThemeProvider>
                </WSAppContextProvider>
            </div>
        );
    }
}

/* Export the Helmet instance we're using here otherwise we'll have helmet
inconsistencies durring SSR */
export const AppHelmet = Helmet;
export default connect()(Sentry.withErrorBoundary(App, { fallback: <p>an error has occurred</p> }));
